import { Zap } from 'lucide-react'
import type { ChargepointSummaryWithDistance, SpeedTier } from '@fuelfinder/shared'
import { operatorLabel } from '../lib/format'

interface CompactChargepointCardProps {
  chargepoint: ChargepointSummaryWithDistance
  onClick: () => void
}

/**
 * Get Tailwind text color class based on speed tier
 */
function getTierColorClass(tier: SpeedTier): string {
  if (tier === 'rapid') return 'text-brand-500'
  if (tier === 'fast') return 'text-brand-400'
  return 'text-amber-500'
}

/**
 * Get Tailwind border color class based on speed tier
 */
function getTierBorderClass(tier: SpeedTier): string {
  if (tier === 'rapid') return 'border-l-brand-500'
  if (tier === 'fast') return 'border-l-brand-400'
  return 'border-l-amber-400'
}

/**
 * Format power in kW
 */
function formatPower(kw: number): string {
  if (kw === 0) return 'N/A'
  return `${Math.round(kw)} kW`
}

/**
 * Format distance in miles
 */
function formatDistance(miles: number): string {
  if (miles < 0.1) return '< 0.1 mi'
  return `${miles.toFixed(1)} mi`
}

/**
 * Render lightning bolt icons for speed tier
 */
function BoltIcons({ tier }: { tier: SpeedTier }) {
  const count = tier === 'rapid' ? 3 : tier === 'fast' ? 2 : 1
  return (
    <span className="inline-flex">
      {Array.from({ length: count }, (_, i) => (
        <Zap key={i} size={14} className="fill-current -ml-0.5 first:ml-0" />
      ))}
    </span>
  )
}

/**
 * Compact chargepoint card for use in the draggable sheet list.
 * Shows operator, power, distance. Single tap opens detail sheet.
 */
export function CompactStationCard({
  chargepoint,
  onClick,
}: CompactChargepointCardProps) {
  const tierColorClass = getTierColorClass(chargepoint.speedTier)
  const borderClass = getTierBorderClass(chargepoint.speedTier)

  return (
    <button
      onClick={onClick}
      className={`compact-station-card w-full text-left bg-white dark:bg-slate-800 border-l-4 ${borderClass} px-3 py-3 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors`}
    >
      <div className="flex items-center justify-between gap-3">
        {/* Left: Operator, name, distance */}
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-slate-900 dark:text-white truncate">
            {operatorLabel(chargepoint.operator)}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
            {chargepoint.name} &middot; {formatDistance(chargepoint.distanceMiles)}
          </p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 truncate">
            {chargepoint.connectorSummary} &middot; {chargepoint.totalConnectors} connector{chargepoint.totalConnectors !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Right: Power and speed tier */}
        <div className="flex flex-col items-end">
          <span className={`text-xl font-bold ${tierColorClass} tabular-nums flex items-center gap-1`}>
            <BoltIcons tier={chargepoint.speedTier} />
            {formatPower(chargepoint.maxPowerKW)}
          </span>
          {!chargepoint.isOperational && (
            <span className="text-xs text-red-500 font-medium mt-0.5">
              Offline
            </span>
          )}
        </div>
      </div>
    </button>
  )
}
